import React, { useState } from 'react';

import { REACT_APP_CLOUD_FRONT } from 'src/constants';
import { IBerryRecommendedItem } from 'src/models/berry.model';
import { isConfigurableBerryItem, sizePlanLabel } from '../helper/berryItemConfig.helper';
import styles from '../berryChat.module.scss';

interface TrayItemCardProps {
  item: IBerryRecommendedItem;
  isAdded: boolean;
  sessionReady: boolean;
  onAddToCart: (item: IBerryRecommendedItem, quantity: number) => Promise<void>;
  onConfigureItem: (item: IBerryRecommendedItem) => void;
}

const TrayItemCard: React.FC<TrayItemCardProps> = ({
  item,
  isAdded,
  sessionReady,
  onAddToCart,
  onConfigureItem,
}) => {
  const needsConfig = isConfigurableBerryItem(item);
  const [imgError, setImgError] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  // Quantity for a direct (non-configurable) add — a configurable item picks
  // its own quantity on the picker's Review step instead (see ItemConfigPicker).
  // Starts at Berry's suggestion for the group (one box per person, enough
  // trays for the headcount) instead of always 1 (B4/B27).
  const startQuantity =
    item.suggested_quantity && item.suggested_quantity > 0 ? item.suggested_quantity : 1;
  const [quantity, setQuantity] = useState(startQuantity);
  // Two-size trays: the sizes Berry recommends for the group ("1 Large + 1
  // Small") and the price of the first — the size Customize opens at (UAT row 12).
  const plan = item.size_plan && item.size_plan.length > 0 ? item.size_plan : null;
  const planLabel = sizePlanLabel(plan);
  const shownPrice = plan?.[0]?.unit_price ?? item.display_price;

  // Calories only when known (Pulse sends 0 for boxes and trays), and never
  // the internal size code ("FULL"/"HALF") — B7/B38.
  // The serving text describes the Large size, so it's left off when Berry
  // suggests starting with a Small.
  const metaParts = [
    item.item_calories ? `${item.item_calories} cal` : null,
    plan && plan[0].size === 'HALF' ? null : item.serving_size_description,
  ].filter(Boolean);

  const handleAddClick = async () => {
    if (!sessionReady) {
      setAddError('Berry needs your location and event date first.');
      return;
    }
    if (needsConfig) {
      onConfigureItem(item);
      return;
    }
    setIsAdding(true);
    setAddError(null);
    try {
      await onAddToCart(item, quantity);
      setQuantity(startQuantity);
    } catch {
      setAddError('Failed to add. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className={styles.itemCard}>
      <div className={styles.itemImageWrapper}>
        {imgError ? (
          <div className={styles.itemImageFallback} aria-hidden="true" />
        ) : (
          <img
            src={`${REACT_APP_CLOUD_FRONT}${item.image_path}`}
            alt={item.name}
            className={styles.itemImage}
            onError={() => setImgError(true)}
          />
        )}
      </div>
      <div className={styles.itemBody}>
        <p className={styles.itemName}>{item.name}</p>
        <p className={styles.itemPrice}>
          ${(shownPrice * (needsConfig ? 1 : quantity)).toFixed(2)}
        </p>
        {needsConfig && planLabel ? (
          <p className={styles.itemMeta}>Suggested for your group: {planLabel}</p>
        ) : (
          needsConfig &&
          startQuantity > 1 && (
            <p className={styles.itemMeta}>Suggested for your group: {startQuantity}</p>
          )
        )}
        {metaParts.length > 0 && (
          <p className={styles.itemMeta}>{metaParts.join(' · ')}</p>
        )}
        {item.dietary_tags.length > 0 && (
          <div className={styles.dietaryTags}>
            {item.dietary_tags.map((tag) => (
              <span key={tag} className={styles.dietaryTag}>
                {tag}
              </span>
            ))}
          </div>
        )}
        {!needsConfig && !isAdded && (
          <div className={styles.qtyStepper}>
            <button
              type="button"
              className={styles.qtyBtn}
              disabled={!sessionReady || isAdding || quantity <= 1}
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              aria-label={`Decrease quantity for ${item.name}`}
            >
              −
            </button>
            <span className={styles.qtyValue}>{quantity}</span>
            <button
              type="button"
              className={styles.qtyBtn}
              disabled={!sessionReady || isAdding}
              onClick={() => setQuantity((q) => q + 1)}
              aria-label={`Increase quantity for ${item.name}`}
            >
              +
            </button>
          </div>
        )}
        <button
          className={needsConfig ? styles.configureBtn : styles.addToCartBtn}
          onClick={handleAddClick}
          disabled={!sessionReady || isAdding || isAdded}
          aria-label={
            isAdded
              ? `${item.name} added to cart`
              : needsConfig
              ? `Customize ${item.name}`
              : `Add ${quantity} ${item.name} to cart`
          }
        >
          {isAdding
            ? 'Adding…'
            : isAdded
            ? 'Added ✓'
            : needsConfig
            ? 'Customize'
            : quantity > 1
            ? `Add ${quantity} to Cart`
            : 'Add to Cart'}
        </button>
        {addError && <p className={styles.cartItemError}>{addError}</p>}
      </div>
    </div>
  );
};

interface RecommendedItemsTrayProps {
  items: IBerryRecommendedItem[];
  /** Items Berry suggested earlier in the chat, not in `items` (B76). */
  earlierItems?: IBerryRecommendedItem[];
  addedItemIds: Set<number>;
  sessionReady: boolean;
  onAddToCart: (item: IBerryRecommendedItem, quantity: number) => Promise<void>;
  onConfigureItem: (item: IBerryRecommendedItem) => void;
  onDismiss: () => void;
}

const RecommendedItemsTray: React.FC<RecommendedItemsTrayProps> = ({
  items,
  earlierItems = [],
  addedItemIds,
  sessionReady,
  onAddToCart,
  onConfigureItem,
  onDismiss,
}) => {
  const [showEarlier, setShowEarlier] = useState(false);
  const renderCard = (item: IBerryRecommendedItem) => (
    <TrayItemCard
      key={item.item_id}
      item={item}
      isAdded={addedItemIds.has(item.item_id)}
      sessionReady={sessionReady}
      onAddToCart={onAddToCart}
      onConfigureItem={onConfigureItem}
    />
  );
  return (
    <div className={styles.recommendedTray} role="region" aria-label="Recommended items">
      <div className={styles.trayHeader}>
        <span className={styles.trayTitle}>Recommended for you</span>
        <button
          className={styles.trayDismissBtn}
          onClick={onDismiss}
          aria-label="Done adding items from this list"
        >
          Done adding
        </button>
      </div>
      <div className={styles.trayItems}>
        {items.map(renderCard)}
        {earlierItems.length > 0 && (
          <button
            type="button"
            className={styles.trayEarlierBtn}
            onClick={() => setShowEarlier((v) => !v)}
            aria-expanded={showEarlier}
          >
            {showEarlier
              ? 'Hide earlier suggestions'
              : `Earlier suggestions (${earlierItems.length})`}
          </button>
        )}
        {showEarlier && earlierItems.map(renderCard)}
      </div>
    </div>
  );
};

export default RecommendedItemsTray;
